import { useState, useEffect } from "react";
import { Button } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import { ReactComponent as InfoIcon } from "@/assets/icons/info.svg";
import {
  successNotification,
  errorNotification,
  cleanNotifications,
} from "@/helpers/notifications";
import SubscriptionDetailsModal from "../SubscripitonDetailsModal/SubscriptionDetailsModal";
import dayjs from "dayjs";
import {
  Subscription as SubscriptionType,
  UserSubscription,
  Order,
} from "@/api/types";

interface SubscriptionProps {
  activeSubscription?: UserSubscription | null;
  subscriptionData?: SubscriptionType;
  orders?: Order[];
}

const Subscription: React.FC<SubscriptionProps> = ({
  activeSubscription,
  subscriptionData,
  orders,
}) => {
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const { cart, addToCart, removeFromCart } = useCart();
  const [subscriptionOrder, setSubscriptionOrder] = useState<Order>();

  useEffect(() => {
    if (!activeSubscription) return;

    setSubscriptionOrder(
      orders?.find((order) =>
        order.orderItems.some((item) => item.subscription)
      )
    );
  }, [activeSubscription, orders]);

  const handleAddToCart = () => {
    if (!subscriptionData) {
      errorNotification({ message: "Molimo pokušajte ponovno!" });
      return;
    }
    const subscription = {
      ...subscriptionData,
      cartId: `subscription-${subscriptionData.id}`,
    };
    addToCart({ ...subscription, ...{ type: "subscription" } });
    successNotification({
      message: (
        <div className="add-to-cart-notification-group">
          <span>Pretplata je dodana u košaricu!</span>
          <span
            className="add-to-cart-notification-link"
            onClick={() => {
              cleanNotifications();
              navigate("/cart");
            }}
          >
            Idi na košaricu
          </span>
        </div>
      ),
    });
  };

  const handleRemoveFromCart = () => {
    removeFromCart(`subscription-${subscriptionData?.id}`);
    successNotification({
      message: "Pretplata je uklonjena iz košarice",
    });
  };

  return (
    <div className="subscription-step-container">
      <div className="subscription-step-title">Pretplata</div>
      {activeSubscription ? (
        <div className="subscription-step-active">
          <div className="subscription-step-active-header">
            <div className="flex flex-col justify-center items-start gap-3">
              <div className="text-[#004a99] text-sm font-bold">
                {activeSubscription?.subscription?.title ??
                  "Godišnja pretplata"}
              </div>
              <div className="w-[410px] text-[#0b1a29] text-sm font-medium">
                Aktivnom pretplatom ostvarujete pravo na čitanje svakog broja
                revije izdanog za vrijeme trajanja pretplate.
              </div>
            </div>
          </div>
          <div className="subscription-step-active-mobile-header">
            <div className="text-[#004a99] text-base font-bold ">
              {activeSubscription?.subscription?.title ?? "Godišnja pretplata"}
            </div>
            <InfoIcon onClick={open} />
          </div>
          <div className="subscription-step-active-data">
            <div className="subscription-step-active-data-property !border-r-0 subscription-property-radius-left">
              <div className="subscription-property-text">Status pretplate</div>
              <div className="subscription-property-text">
                {activeSubscription?.canceledAt ? "Otkazano" : "Aktivno"}
              </div>
            </div>
            <div className="subscription-step-active-data-property !border-r-0">
              <div className="subscription-property-text">Aktivirano</div>
              <div className="subscription-property-text">
                {dayjs(activeSubscription?.startDate).format("DD.MM.YYYY.")}
              </div>
            </div>
            <div className="subscription-step-active-data-property !border-r-0">
              <div className="subscription-property-text">Aktivno do</div>
              <div className="subscription-property-text">
                {dayjs(activeSubscription?.endDate).format("DD.MM.YYYY.")}
              </div>
            </div>
            <div className="subscription-step-active-data-property subscription-property-radius-right">
              <div className="subscription-property-text">Cijena</div>
              <div className="subscription-property-text">
                {activeSubscription?.subscription?.price}€
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="subscription-step-unactive-data">
          <div className="no-active-sub-text">
            Trenutno nema aktivne pretplate.
          </div>
          <div className="available-subscriptions">
            Dostupne opcije pretplate
          </div>
          <div className="yearly-subscription-box">
            <div className="yearly-subscription-box-info">
              <div className="yearly-subscription-box-title ">
                {subscriptionData?.title ?? "Godišnja pretplata"}
              </div>
              <div className="text-[#0b1a29] text-sm font-medium">
                Kupnjom pretplate ostvarujete pravo na čitanje svakog broja
                revije izdanog za vrijeme trajanja pretplate.
              </div>
            </div>
            <div className="yearly-subscription-button-container">
              <div className="yearly-subscription-price-container">
                <span className="text-[#004a99] text-sm font-bold">
                  {subscriptionData?.price}€
                </span>
                <span className="text-[#004a99] text-sm font-medium">
                  (jednokratno plaćanje)
                </span>
              </div>
              <div className="yearly-subscription-buttons-inner-container">
                {cart
                  .map((item) => item.cartId)
                  .includes(`subscription-${subscriptionData?.id}`) ? (
                  <Button
                    variant="filled"
                    color="#004a99"
                    radius="xl"
                    size="md"
                    className="subscription-button"
                    leftSection={<DeleteIcon fill="white" />}
                    onClick={() => handleRemoveFromCart()}
                  >
                    Ukloni pretplatu
                  </Button>
                ) : (
                  <Button
                    variant="filled"
                    color="#004a99"
                    radius="xl"
                    size="md"
                    className="subscription-button"
                    onClick={() => handleAddToCart()}
                  >
                    Pretplati se
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {activeSubscription && subscriptionOrder && (
        <SubscriptionDetailsModal
          opened={opened}
          onClose={close}
          subscriptionDetails={activeSubscription}
          subscriptionOrder={subscriptionOrder}
        />
      )}
    </div>
  );
};

export default Subscription;
